import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { filter, skipWhile, take } from 'rxjs/operators';
import { WebSocketNotificationTypeEnum } from 'src/app/core/websocket';
import { WebSocketService } from 'src/app/core/websocket/websocket.service';
import { SelectInPopoverOption } from 'src/app/shared/components/select-in-popover/select-in-popover-option';
import { SiteTypeEnum } from 'src/app/shared/enums';
import { MessageSender } from 'src/app/shared/models/misc.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { ErrorHandlingService } from 'src/app/shared/services/error-handling.service';
import { SuggestAnswerService } from 'src/app/shared/services/suggest-answer.service';
import { environment } from 'src/environments/environment';

import { AIAssistantAnswerStatusEnum } from '../../shared/models/ai-assistant-answer-status.enum';
import { TaskProcessContext } from '../../shared/task-process-context.class';
import { TaskProcessService } from '../../shared/task-process.service';
import { SelectReplyToModalComponent } from '../select-reply-to-modal/select-reply-to-modal.component';
import { LineFragment, LineFragmentType } from './line-fragment';

export interface ReplyInPopoverOption extends SelectInPopoverOption {
  isPrivate: boolean;
}
/**
 * Text above answer editor in processing page, and AI ssistant button and suggestion.
 *
 * Reply
 * In : private/public
 * As : site name
 * To : user
 * Line : just a line of text
 *
 * eg : "Reply in private as Company to John Doe"
 *
 * => hence the name of the component...
 */
@Component({
  selector: 'app-reply-in-as-to-line',
  templateUrl: './reply-in-as-to-line.component.html',
  styleUrls: ['./reply-in-as-to-line.component.scss'],
})
export class ReplyInAsToLineComponent implements OnInit, OnDestroy {
  @Input() taskProcessContext: TaskProcessContext;
  @Input() isWhatsappTemplateMode = false;

  LineFragmentType = LineFragmentType;
  fragments: LineFragment[];

  replyInOptions: ReplyInPopoverOption[] = [];
  selectedReplyInOption;

  replyAsOptions: SelectInPopoverOption[] = [];
  selectedReplyAsOption: SelectInPopoverOption;

  replyToList: MessageSender[];
  replyToName: string;
  replyToNOtherNamesLabel: string;

  aiAssistantButtonMarginLeft = 15;
  canUseAIAssistant = false;
  aiTooltip;
  aiProcessing = false;

  private inPublicOption: ReplyInPopoverOption;
  private inPrivateOption: ReplyInPopoverOption;
  private aiSubscriptions: Subscription;
  private subscriptions = new Subscription();

  @HostListener('window:resize', ['$event'])
  onResizePositionTheAIAssistantButton() {
    // Allows to align the button with the editor right border side.
    const sendButtons: any = document.querySelector('.send-buttons-column');

    if (sendButtons) {
      this.aiAssistantButtonMarginLeft = sendButtons.offsetWidth - 8; // 8 => .reply to line padding
    }
  }

  constructor(
    public basicService: BasicService,
    public taskProcessService: TaskProcessService,
    private webSocketService: WebSocketService,
    private suggestAnswerService: SuggestAnswerService
  ) {
    ErrorHandlingService;
    this.inPublicOption = {
      isPrivate: false,
      label: this.basicService.translate('m/task-process/reply-in-as-to-line/public'),
    };
    this.inPrivateOption = {
      isPrivate: true,
      label: this.basicService.translate('m/task-process/reply-in-as-to-line/private'),
    };
  }

  ngOnInit() {
    const textAndPlaceholders = this.basicService.translate('m/task-process/reply-in-as-to-line').split(/({\w+})/);
    this.fragments = textAndPlaceholders.map(fragment => new LineFragment(fragment));
    this.initReplyAs();
    this.initReplyTo();
    this.initReplyIn(); // needs replyTo list

    if (!environment.isNativeApp && !this.isWhatsappTemplateMode) {
      this.subscriptions.add(
        this.suggestAnswerService.AissitantConfigForTeamBS.subscribe(config => {
          if (config.aiAgentId) {
            this.canUseAIAssistant = true;
            this.aiSubscriptions = new Subscription();
            this.showAIAssistantAlignedWithEditor();
            this.listenAIAssistantSuggestionsFromWS();
          } else {
            this.canUseAIAssistant = false;
            this.aiSubscriptions?.unsubscribe();
          }
        })
      );
    }
  }

  askForAIAssistant() {
    if (this.basicService.answerSuggestionPending[this.taskProcessContext.processedTaskId]?.minimized) {
      this.suggestAnswerService.restoreAIAssistantAnswer(this.taskProcessContext.processedTaskId);
    } else {
      this.updateAIAssistantButtonLabel(true);
      const taskId = this.taskProcessContext.processedTaskId;
      this.triggerAIAssistantAnswer(taskId);
    }
  }

  private updateAIAssistantButtonLabel(processing: boolean) {
    this.aiProcessing = processing;
    let key = '';
    if (processing) {
      key = 'm/task-process/ai-assistant/button-tooltip/processing';
    } else if (this.basicService.answerSuggestionPending[this.taskProcessContext.processedTaskId]?.minimized) {
      key = 'm/task-process/ai-assistant/button-tooltip/restore';
    } else {
      key = 'm/task-process/ai-assistant/button-tooltip/waiting';
    }

    this.aiTooltip = this.basicService.translate(key);
  }

  /**
   * Allows to call the Generative AI with the taskId. This G. AI should work with this one to gather the context and compute a suggested answer.
   * The answer will is retrieved through a WebSocket ws.ai.suggest-answer
   * @param taskId The requested task id.
   */
  private triggerAIAssistantAnswer = (taskId: number) => {
    this.suggestAnswerService.getSuggestAnswer(taskId);
  };

  async showReplyInList(event) {
    event.preventDefault();
    const modalResult = await this.basicService.selectInPopover(event, this.replyInOptions);
    if (modalResult.data) {
      this.updateSelectedReplyIn(modalResult.data);
    }
  }

  async showReplyAsList(event) {
    event.preventDefault();
    const modalResult = await this.basicService.selectInPopover(event, this.replyAsOptions);
    if (modalResult.data) {
      this.updateSelectedReplyAs(modalResult.data);
      this.taskProcessService
        .canSendPrivateMessageAsOtherSite(modalResult.data.siteId, this.replyToList[0])
        .subscribe(canReplyInPrivate => this.updateReplyInFollowingReplyAsUpdate(canReplyInPrivate));
    }
  }

  async showReplyToList(event) {
    event.preventDefault();
    await this.basicService.popoverWithResult(event, SelectReplyToModalComponent, { replyToList: this.replyToList });
    this.updateReplyToNOtherNamesLabel();
  }

  private initReplyIn() {
    if (this.taskProcessContext.isPublic() && this.taskProcessContext.site.type === SiteTypeEnum.Twitter) {
      // 00002002 - Force fetching the info from Twitter as our database isn't up-to-date
      // (it isn't initialized correctly, and it is not updated when a user follows/unfollows the brand)
      this.taskProcessService
        .canSendPrivateMessageAsOtherSite(this.taskProcessContext.site.id, this.replyToList[0])
        .subscribe(canReplyInPrivate =>
          this.resetReplyIn(canReplyInPrivate, this.taskProcessContext.processedTaskInfo.mustSwitchFromPublicToPrivate)
        );
    } else {
      this.resetReplyIn(
        this.taskProcessContext.processedTaskInfo.canSwitchFromPublicToPrivate,
        this.taskProcessContext.processedTaskInfo.mustSwitchFromPublicToPrivate
      );
    }
  }

  private resetReplyIn(canSwitchFromPublicToPrivate, mustSwitchFromPublicToPrivate) {
    this.replyInOptions = [];
    this.inPublicOption.isSelected = false;
    this.inPrivateOption.isSelected = false;
    const previouslySelectedOption = this.selectedReplyInOption;
    if (this.taskProcessContext.isPublic()) {
      if (mustSwitchFromPublicToPrivate) {
        this.replyInOptions.push(this.inPrivateOption);
      } else {
        this.replyInOptions.push(this.inPublicOption);
        if (canSwitchFromPublicToPrivate) {
          this.replyInOptions.push(this.inPrivateOption);
        }
      }
    } else {
      this.replyInOptions.push(this.inPrivateOption);
    }

    if (this.replyInOptions.includes(previouslySelectedOption)) {
      this.updateSelectedReplyIn(previouslySelectedOption);
    } else {
      this.updateSelectedReplyIn(
        this.taskProcessContext.isPrivate() || mustSwitchFromPublicToPrivate
          ? this.inPrivateOption
          : this.inPublicOption
      );
    }
    this.selectedReplyInOption.isSelected = true;
  }

  private updateSelectedReplyIn(option) {
    this.selectedReplyInOption = option;
    this.taskProcessContext.switchReplyIn(option.isPrivate);
  }

  private updateReplyInFollowingReplyAsUpdate(canReplyInPrivate) {
    if (this.taskProcessContext.isPublic()) {
      const previouslySelectedOption = this.selectedReplyInOption;
      this.resetReplyIn(canReplyInPrivate, this.taskProcessContext.processedTaskInfo.mustSwitchFromPublicToPrivate);
      if (previouslySelectedOption !== this.selectedReplyInOption) {
        this.taskProcessContext.switchReplyIn(this.selectedReplyInOption.isPrivate);
        this.basicService.modalAlertWithOK(null, 'm/task-process/reply-in-updated-after-reply-as-update');
      }
    }
  }

  private initReplyAs() {
    const config = this.taskProcessContext.processedTaskInfo.replyAsConfig;
    for (const site of config.allowedReplyAsSites) {
      this.replyAsOptions.push({
        label: site.name,
        siteId: site.id,
        isSelected: site.id === config.intialReplyAsSite.id,
      } as SelectInPopoverOption);
    }
    this.updateSelectedReplyAs(this.replyAsOptions.find(option => option.isSelected));
  }

  private updateSelectedReplyAs(option) {
    this.selectedReplyAsOption = option;
    this.taskProcessContext.replyAsSiteId = option.siteId;
  }

  private initReplyTo() {
    this.replyToList = this.taskProcessContext.processedTaskInfo.replyToList;
    this.replyToList.forEach(user => (user.isSelected = true));
    this.replyToName = this.handleOrName(this.replyToList[0]);
    this.updateReplyToNOtherNamesLabel();
  }

  private updateReplyToNOtherNamesLabel() {
    const n = this.replyToList.filter(user => user.isSelected).length;
    let label = null;
    if (n === 1 && this.replyToList.length > 1) {
      label = `(${this.basicService.translate('m/task-process/reply-to-add-more')})`;
    } else if (n === 2) {
      label = this.basicService.translate('m/task-process/reply-to-1-more');
    } else if (n > 2) {
      label = this.basicService.translate('m/task-process/reply-to-n-more', { n: String(this.replyToList.length - 1) });
    }
    // If there is only one replyTo user in the list, the label stays null and the template won't display the link at all
    this.replyToNOtherNamesLabel = label;
  }

  private handleOrName(user: MessageSender) {
    return user.handle ? user.handle : user.name;
  }

  private showAIAssistantAlignedWithEditor() {
    this.aiSubscriptions.add(
      interval(300)
        .pipe(
          // Waiting for the editor and the buttons to be displayed
          skipWhile(() => {
            let result = null;
            try {
              result =
                !!document.querySelector('app-codemirror-wrapper') &&
                !!document.querySelector('app-single-message-editor>div.buttons-line:not([hidden])') &&
                !!document.querySelector('.send-buttons-column');
            } catch {
              result = null;
            }
            return !result;
          }),
          take(1)
        )
        .subscribe({
          next: () => {
            this.updateAIAssistantButtonLabel(false);
            this.onResizePositionTheAIAssistantButton();
          },
        })
    );
  }

  private listenAIAssistantSuggestionsFromWS() {
    this.aiSubscriptions.add(
      this.webSocketService.message$
        .pipe(
          filter(
            (event: any) =>
              this.taskProcessContext && event.type === WebSocketNotificationTypeEnum.AIAssistantSuggestion
          )
        )
        .subscribe({
          next: event => {
            const { data, task_id } = event;
            const taskId = task_id;
            if (data.status === AIAssistantAnswerStatusEnum.STARTED) {
              this.updateAIAssistantButtonLabel(true);
            } else {
              if (this.basicService.answerSuggestionPending[taskId]) {
                if (
                  this.basicService.answerSuggestionPending[taskId] &&
                  [AIAssistantAnswerStatusEnum.FINISHED, AIAssistantAnswerStatusEnum.FAILED].includes(data.status)
                ) {
                  this.basicService.answerSuggestionPending[taskId].received = true;
                }

                if (data.status === AIAssistantAnswerStatusEnum.FINISHED) {
                  // If the answer arrives after 60 seconds it is considered as a timeout, the pending result for the task id is null.
                  if (!data.answer) {
                    this.basicService.toastInformation('m/task-process/ai-assistant/answer-empty');
                  }
                  this.basicService.answerSuggestionPending[taskId].resultBS.next(data);
                } else if (data.status === AIAssistantAnswerStatusEnum.MINIMIZED) {
                  this.basicService.answerSuggestionPending[taskId].minimized = true;
                } else if (data.status === AIAssistantAnswerStatusEnum.RESTORED) {
                  this.basicService.answerSuggestionPending[taskId].minimized = false;
                  this.basicService.answerSuggestionPending[taskId].resultBS.next(
                    this.basicService.answerSuggestionPending[taskId].resultBS.value
                  );
                } else if (data.status === AIAssistantAnswerStatusEnum.FAILED) {
                  this.basicService.toastAlert('m/task-process/ai-assistant/trigger-error');
                } else if (data.status === AIAssistantAnswerStatusEnum.EXPIRED) {
                  this.basicService.answerSuggestionPending[taskId].expiredSubscription.unsubscribe();
                  this.basicService.answerSuggestionPending[taskId] = null;
                  this.basicService.toastInformation('m/task-process/ai-assistant/answer-timeout');
                }
              }

              this.updateAIAssistantButtonLabel(false);
            }
          },
        })
    );
  }

  ngOnDestroy() {
    this.aiSubscriptions?.unsubscribe();
    this.subscriptions?.unsubscribe();
  }
}
